import { useCallback, useEffect, useMemo, useState } from "react";

import type {
  ConstructionFormState,
  LayeredConstructionCatalogItem,
  LayeredConstructionTechnology,
  LayerFormValue,
} from "../model/types";
import type { LayeredConstructionVariant } from "../model/variants";

import { applyCatalogItemToFormState, defaultConstructionFormState, mapTechnologyToFormState } from "../lib/configForm";
import { findCatalogItem, findCatalogLayerByMaterial, getCatalogMaterialNames } from "../lib/configOptions";
import { buildConstructionSavePayload } from "../lib/configPayload";
import { createEmptyLayer } from "../lib/layers";

type UseLayeredConstructionFormOptions = {
  catalog: LayeredConstructionCatalogItem[];
  enabled: boolean;
  hasTechnology: boolean;
  initialTechnology?: LayeredConstructionTechnology | null;
  isCatalogLoading: boolean;
  isLoading: boolean;
  recordKey?: string;
  variant: LayeredConstructionVariant;
};

export const useLayeredConstructionForm = ({
  catalog,
  enabled,
  hasTechnology,
  initialTechnology,
  isCatalogLoading,
  isLoading,
  recordKey,
  variant,
}: UseLayeredConstructionFormOptions) => {
  const [formState, setFormState] = useState<ConstructionFormState>(defaultConstructionFormState());
  const loadKey = `${recordKey ?? ""}:${initialTechnology?.id ?? "new"}:${hasTechnology}`;

  useEffect(() => {
    if (!enabled || isLoading || isCatalogLoading) return;

    if (!initialTechnology) {
      if (!hasTechnology) setFormState(defaultConstructionFormState());
      return;
    }

    setFormState(mapTechnologyToFormState(initialTechnology, catalog));
  }, [catalog, enabled, hasTechnology, initialTechnology, isCatalogLoading, isLoading, loadKey]);

  const selectType = useCallback(
    (typeName: string) => {
      setFormState((current) => {
        const catalogItem = findCatalogItem(catalog, typeName);
        if (!catalogItem) return { ...current, typeName };
        return applyCatalogItemToFormState(current, catalogItem);
      });
    },
    [catalog]
  );

  const setColor = useCallback((color: string) => setFormState((current) => ({ ...current, color })), []);

  const updateLayer = useCallback(
    (index: number, field: keyof LayerFormValue, value: string) =>
      setFormState((current) => ({
        ...current,
        layers: current.layers.map((layer, layerIndex) =>
          layerIndex === index ? { ...layer, [field]: value } : layer
        ),
      })),
    []
  );

  const selectLayerMaterial = useCallback(
    (index: number, materialName: string) => {
      setFormState((current) => {
        const catalogLayer = findCatalogLayerByMaterial(catalog, materialName, current.typeName);
        return {
          ...current,
          layers: current.layers.map((layer, layerIndex) => {
            if (layerIndex !== index) return layer;
            if (!catalogLayer) return { ...layer, material: materialName };
            return {
              density: catalogLayer.density,
              lambda: catalogLayer.lambda,
              material: materialName,
              specificHeat: catalogLayer.specificHeat,
              thickness: layer.thickness,
            };
          }),
        };
      });
    },
    [catalog]
  );

  const addLayer = useCallback(
    () => setFormState((current) => ({ ...current, layers: [...current.layers, createEmptyLayer()] })),
    []
  );

  const removeLayer = useCallback(
    (index: number) =>
      setFormState((current) => ({
        ...current,
        layers: current.layers.filter((_, layerIndex) => layerIndex !== index),
      })),
    []
  );

  const typeNames = useMemo(() => [...new Set(catalog.map((item) => item.typeName))].sort(), [catalog]);
  const materialNames = useMemo(() => getCatalogMaterialNames(catalog), [catalog]);

  return {
    addLayer,
    formState,
    mapPayload: () => buildConstructionSavePayload({ catalog, formState, variant }),
    materialNames,
    removeLayer,
    selectLayerMaterial,
    selectType,
    setColor,
    typeNames,
    updateLayer,
  };
};

export type LayeredConstructionForm = ReturnType<typeof useLayeredConstructionForm>;
